import Link, { LinkProps } from "next/link"
import { useRouter } from "next/router"
import { MouseEventHandler, ReactNode, useCallback, useMemo } from "react"
import { P, match } from "ts-pattern"

import LumenLogo from "@/components/icons/LumenLogo"
import LumenLogoMark from "@/components/icons/LumenLogoMark"
import filterNulls from "@/lib/filterNulls"
import { flaskUrl, url } from "@/lib/url"
import {
  PortfolioListQuery,
  ViewType,
  useCurrentUserQuery,
  usePortfolioListQuery,
  usePortfoliosAcceptingOffersQuery,
} from "@/v2/graphql/public-generated-rq"
import { useFlags } from "@/v2/providers/FeatureFlags"
import { useInitialPortfolioPid } from "@/v2/providers/InitialPortfolioPid"
import { Drawer, Select, Text, twMerge } from "@lumen-energy/design-system"
import * as NavigationMenu from "@radix-ui/react-navigation-menu"
import * as Sentry from "@sentry/nextjs"
import {
  Icon,
  IconArrowBackUp,
  IconBolt,
  IconBuilding,
  IconChartBar,
  IconHelpOctagon,
  IconHome,
  IconList,
  IconSend,
  IconSettings,
  IconSun,
  IconTag,
  IconX,
} from "@tabler/icons-react"

import { SettingsTab } from "../../settings/Settings"
import { useAppLayout } from "./AppLayoutContext"
import { MobileHeaderDropdown, SidebarFooterUserDropdown } from "./Header"
import { portfolioUrl } from "./util"

type Portfolio = PortfolioListQuery["portfolios"][number]

const usePortfolioPid = () => {
  // We might be in a route that has no portfolioPid, so we need to check
  // if we have a portfolioPid in localstorage, and if not, use the first
  // portfolio in the user portfolio list
  const router = useRouter()
  const { pid: initialPid } = useInitialPortfolioPid()
  const { data } = usePortfolioListQuery()
  const firstPortfolioPid = data?.portfolios?.[0]?.pid

  let queryPortfolioPid =
    typeof router.query.portfolioPid === "string"
      ? router.query.portfolioPid
      : undefined

  return queryPortfolioPid || initialPid || firstPortfolioPid || ""
}

const DesktopSidebarWrapper = ({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) => {
  return (
    <aside
      className={twMerge(
        "sticky top-0 isolate z-10 hidden h-screen w-60 shrink-0 flex-col overflow-auto bg-brand-primary-900 text-white md:flex",
      )}
    >
      <NavigationMenu.Root
        className={twMerge(
          "group flex h-full w-full flex-col bg-brand-primary-900 text-white [&>:last-child]:flex [&>:last-child]:flex-grow [&>:last-child]:flex-col",
          className,
        )}
        orientation="vertical"
      >
        <NavigationMenu.List className="flex flex-grow flex-col">
          {children}
        </NavigationMenu.List>
      </NavigationMenu.Root>
    </aside>
  )
}

const MobileSidebarWrapper = ({
  className,
  children,
  open,
  onClose,
}: {
  className?: string
  children: ReactNode
  open?: boolean
  onClose?: () => void
}) => {
  return (
    <Drawer.Root open={open} onOpenChange={(value) => !value && onClose?.()}>
      <Drawer.Content position="left" className="block w-full max-w-md md:hidden">
        <NavigationMenu.Root
          className={twMerge(
            "group flex min-h-full w-full flex-col bg-brand-primary-900 text-white [&>:last-child]:flex [&>:last-child]:flex-grow [&>:last-child]:flex-col",
            className,
          )}
          orientation="vertical"
        >
          <div className="flex h-header items-center border-b border-b-colors-border-secondary p-4">
            <Drawer.Trigger asChild>
              <button aria-label="Close sidebar" className="m-2 cursor-pointer">
                <IconX size={24} stroke={1.5} />
              </button>
            </Drawer.Trigger>
            <LumenLogoMark className="h-8 w-auto" />
            <div className="flex grow justify-end">
              <MobileHeaderDropdown />
            </div>
          </div>
          <NavigationMenu.List className="flex flex-grow flex-col">
            {children}
          </NavigationMenu.List>
        </NavigationMenu.Root>
      </Drawer.Content>
    </Drawer.Root>
  )
}

export const Sidebar = ({
  className,
  open,
  onClose,
}: {
  className?: string
  open?: boolean
  onClose?: () => void
}) => {
  const { view } = useAppLayout()
  const portfolioPid = usePortfolioPid()

  const content = (
    <div className="flex flex-grow flex-col py-6">
      <Link
        href={match(view)
          .with(P.union(ViewType.Offtaker, undefined), () =>
            url(["/o/portfolio/[portfolioPid]", { portfolioPid }]),
          )
          .with(ViewType.Investor, () =>
            url(["/s/portfolio/[portfolioPid]", { portfolioPid }]),
          )
          .exhaustive()}
        className="hidden px-5 pb-3 md:block"
      >
        <LumenLogo className="h-9 w-auto" dark />
      </Link>
      {match(view)
        .with(ViewType.Offtaker, () => <OfftakerSidebar />)
        .with(ViewType.Investor, () => <InvestorSidebar />)
        .otherwise(() => (
          <DefaultSidebar />
        ))}

      <div className="flex-grow border-b border-brand-secondary-900" />
      <SidebarFooter />
    </div>
  )

  return (
    <>
      <DesktopSidebarWrapper className={className}>{content}</DesktopSidebarWrapper>
      <MobileSidebarWrapper open={open} onClose={onClose} className={className}>
        {content}
      </MobileSidebarWrapper>
    </>
  )
}

const SidebarFooter = () => {
  const { data: userData } = useCurrentUserQuery()
  const org = userData?.currentUser?.org

  return (
    <>
      <div className="md:py-6">
        <MenuItem
          href={url(["/settings?tab=[tab]", { tab: SettingsTab.Users }])}
          isActive={false}
          icon={IconSend}
        >
          Share with colleague
        </MenuItem>
        <MenuItem
          href={flaskUrl(["/help/product"])}
          isActive={false}
          icon={IconHelpOctagon}
        >
          Help center
        </MenuItem>
        {org && (
          <MenuItem href={url(["/settings"])} isActive={false} icon={IconSettings}>
            Settings
          </MenuItem>
        )}
      </div>
      <div className="hidden md:block">
        <SidebarFooterUserDropdown />
      </div>
    </>
  )
}

const OfftakerSidebar = () => {
  const portfolioPid = usePortfolioPid()
  const router = useRouter()

  return (
    <>
      <PortfoliosSelect />
      <MenuItem
        href={url(["/o/portfolio/[portfolioPid]", { portfolioPid }])}
        isActive={router.pathname === "/o/portfolio/[portfolioPid]"}
        icon={IconHome}
      >
        Home
      </MenuItem>
      <MenuItem
        href={url(["/o/portfolio/[portfolioPid]/properties", { portfolioPid }])}
        isActive={router.pathname.startsWith("/o/portfolio/[portfolioPid]/properties")}
        icon={IconTag}
      >
        Offers
      </MenuItem>
    </>
  )
}

const InvestorSidebar = () => {
  const portfolioPid = usePortfolioPid()
  const router = useRouter()
  const flags = useFlags()

  return (
    <>
      <PortfoliosSelect />
      <MenuItem
        href={url(["/s/portfolio/[portfolioPid]", { portfolioPid }])}
        isActive={router.pathname === "/s/portfolio/[portfolioPid]"}
        icon={IconChartBar}
      >
        Portfolio progress
      </MenuItem>
      <MenuItem
        href={url(["/s/portfolio/[portfolioPid]/properties", { portfolioPid }])}
        isActive={router.pathname.startsWith("/s/portfolio/[portfolioPid]/properties")}
        icon={IconBuilding}
      >
        Property list
      </MenuItem>
      {(flags.Shine.DashboardPage || flags.Shine.Portfolios.includes(portfolioPid)) && (
        <MenuItem
          href={url(["/s/portfolio/[portfolioPid]/shine", { portfolioPid }])}
          isActive={router.pathname === "/s/portfolio/[portfolioPid]/shine"}
          icon={IconSun}
        >
          Shine
        </MenuItem>
      )}
      <MenuItem
        href={url(["/data-collection/energy-usage"])}
        isActive={router.pathname.startsWith("/data-collection/energy-usage")}
        icon={IconBolt}
      >
        Energy forms
      </MenuItem>
      <MenuItem
        href={url(["/data-collection/forms"])}
        isActive={router.pathname.startsWith("/data-collection/forms")}
        icon={IconList}
      >
        Qualification forms
      </MenuItem>
    </>
  )
}

const DefaultSidebar = () => {
  const router = useRouter()

  const handleExit: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault()
    router.push(url(["/s/portfolio"]))
  }

  return (
    <div>
      {router.pathname.startsWith("/settings") && (
        <a
          href="#"
          className={twMerge(
            "flex h-[52px] cursor-pointer items-center gap-2 px-5 py-4 transition-all focus-within:bg-brand-secondary-900 hover:bg-brand-secondary-900",
          )}
          onClick={handleExit}
        >
          <div className="w-5">
            <IconArrowBackUp size={20} />
          </div>
          <span
            className={twMerge(
              "whitespace-nowrap opacity-100 transition-all duration-300",
            )}
          >
            Exit Settings
          </span>
        </a>
      )}
    </div>
  )
}

interface MenuItemProps {
  children: ReactNode
  href: LinkProps["href"]
  icon?: Icon
  isActive: boolean
}

const MenuItem = ({ children, href, icon, isActive }: MenuItemProps) => {
  const IconComp = icon

  return (
    <NavigationMenu.Item
      className={twMerge(
        "border-r-4 border-solid border-transparent focus-within:bg-brand-secondary-900 hover:bg-brand-secondary-900",
        { "border-r-brand-secondary-400 bg-brand-secondary-900": isActive },
      )}
    >
      <Link legacyBehavior href={href} passHref>
        <NavigationMenu.Link
          className={twMerge(
            "flex h-[52px] items-center gap-2 px-5 py-4 transition-all",
          )}
        >
          <div className="w-6">
            {IconComp && <IconComp size={24} strokeWidth={1} />}
          </div>
          <Text.Body2 className="whitespace-nowrap opacity-100 transition-all duration-300">
            {children}
          </Text.Body2>
        </NavigationMenu.Link>
      </Link>
    </NavigationMenu.Item>
  )
}

const PortfoliosSelect = () => {
  const portfolioPid = usePortfolioPid()

  const { setPid: setInitialPid } = useInitialPortfolioPid()
  const router = useRouter()
  const appLayout = useAppLayout()
  const { data: userData } = useCurrentUserQuery()

  const handleSelectPortfolio = useCallback(
    (portfolio: Portfolio | null) => {
      if (portfolio) {
        const portfolioPid = portfolio.pid
        setInitialPid(portfolioPid)

        const url = portfolioUrl(portfolio, appLayout.view)
        if (url) {
          router.push(url)
        } else {
          console.log(
            `Invalid view setup: ` +
              JSON.stringify({
                appLayoutView: appLayout.view,
                portfolioViews: portfolio.views,
                user: userData,
              }),
          )
          Sentry.captureMessage(
            `Invalid view setup: ` +
              JSON.stringify({
                appLayoutView: appLayout.view,
                portfolioViews: portfolio.views,
                user: userData,
              }),
          )
        }
      }
    },
    [appLayout.view, userData, router, setInitialPid],
  )

  const { data: portfolioListData } = usePortfolioListQuery()
  const { data: offtakerPortfoliosData } = usePortfoliosAcceptingOffersQuery()

  const directMembershipPortfolios = useMemo(
    () => portfolioListData?.portfolios ?? [],
    [portfolioListData],
  )
  // Do not show portfolios that the user is already a member of as part of offtakerPortfolios.
  const offtakerPortfolios = useMemo(
    () =>
      (offtakerPortfoliosData?.portfoliosAcceptingOffers ?? []).filter(
        (offtakerPortfolio) =>
          !directMembershipPortfolios.some(
            (portfolio) => portfolio.pid === offtakerPortfolio.pid,
          ),
      ),
    [directMembershipPortfolios, offtakerPortfoliosData],
  )

  const options = useMemo(() => {
    return filterNulls([
      directMembershipPortfolios.length > 0
        ? {
            label: "My portfolios",
            options: directMembershipPortfolios,
          }
        : null,
      offtakerPortfolios.length > 0
        ? {
            label: "Offtakers",
            options: offtakerPortfolios,
          }
        : null,
    ])
  }, [directMembershipPortfolios, offtakerPortfolios])

  const portfolios = useMemo(() => {
    return [...directMembershipPortfolios, ...offtakerPortfolios]
  }, [directMembershipPortfolios, offtakerPortfolios])

  const selectedPortfolio = useMemo(
    () => portfolios.find((item) => item.pid === portfolioPid),
    [portfolioPid, portfolios],
  )

  return (
    <div className="flex justify-center px-5 py-4">
      {portfolios.length === 1 ? (
        <Text.LabelLg className="">{selectedPortfolio?.title}</Text.LabelLg>
      ) : (
        <Select<
          Portfolio,
          false,
          {
            label: string
            options: Portfolio[]
          }
        >
          options={options}
          filterOption={(option, filter) =>
            option.data.title.toLowerCase().includes(filter.toLowerCase())
          }
          formatOptionLabel={(item) => `${item.title}`}
          onChange={handleSelectPortfolio}
          value={selectedPortfolio}
          classNames={{
            container: () => /*tw*/ "w-full",
            control: () =>
              /*tw*/ "bg-brand-primary-900 text-white hover:bg-brand-secondary-900 border-brand-secondary-700",
            menu: () => /*tw*/ "sm:min-w-[360px]",
          }}
          menuPortalTarget={document.body}
        />
      )}
    </div>
  )
}
